<template>
  <div
    class="rounded-2xl border border-ink/10 bg-white/70 p-5"
    data-testid="season-data"
  >
    <div v-if="loading" class="text-sm text-slate-500">
      {{ t('season-data.loading') }}
    </div>
    <p
      v-else-if="error"
      class="text-sm font-semibold text-rose-600"
      data-testid="season-data-error"
    >
      {{ error }}
    </p>
    <div v-else>
      <dl class="grid gap-4 sm:grid-cols-2">
        <div
          v-for="row in baseRows"
          :key="row.key"
          class="flex flex-col gap-1"
          :data-testid="row.testId"
        >
          <dt class="text-[0.65rem] uppercase tracking-[0.2em] text-slate-500">
            {{ row.label }}
          </dt>
          <dd class="text-sm font-semibold text-neutral-900">
            {{ row.value }}
          </dd>
        </div>
      </dl>
      <button
        v-if="!showExtra && extraRows.length > 0"
        class="mt-4 inline-flex items-center justify-center border-0 bg-transparent p-0 text-xs font-semibold uppercase tracking-[0.2em] text-neutral-900/70 underline underline-offset-4 transition hover:text-neutral-900 sm:rounded-full sm:border sm:border-ink/20 sm:bg-white sm:px-4 sm:py-2 sm:text-xs sm:tracking-[0.2em] sm:text-neutral-900 sm:no-underline sm:hover:border-ink/40"
        type="button"
        data-testid="season-extra-toggle"
        @click="showExtra = true"
      >
        {{ t('season-data.show-extra') }}
      </button>
      <dl v-if="showExtra" class="mt-4 grid gap-4 sm:grid-cols-2">
        <div
          v-for="row in extraRows"
          :key="row.key"
          class="flex flex-col gap-1"
          :data-testid="row.testId"
        >
          <dt class="text-[0.65rem] uppercase tracking-[0.2em] text-slate-500">
            {{ row.label }}
          </dt>
          <dd class="text-sm font-semibold text-neutral-900">
            {{ row.value }}
          </dd>
        </div>
      </dl>
      <p
        v-if="baseRows.length === 0 && extraRows.length === 0"
        class="mt-3 text-sm text-slate-500"
        data-testid="season-data-empty"
      >
        {{ t('season-data.empty') }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { fetchPlayerSeasonStats } from "../services/api";
import { formatSeason, parseSeasonLabel } from "../utils/season";
import { isNationalTeamStat } from "../utils/isNationalTeam";
import { useText } from "../composables/useText.js";

const { t } = useText();

const props = defineProps({
  playerId: {
    type: [String, Number],
    default: "",
  },
  season: {
    type: [String, Number],
    default: "",
  },
  leagueLabel: {
    type: String,
    default: "",
  },
  seasonLabel: {
    type: String,
    default: "",
  },
  teamId: {
    type: String,
    default: "",
  },
  position: {
    type: String,
    default: "",
  },
});

const loading = ref(false);
const error = ref("");
const totals = ref(null);
const teamNames = ref([]);
const leagueNames = ref([]);
const showExtra = ref(false);
let requestId = 0;

const normalizeSeasonYear = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const text = String(value);
  if (text.includes("-")) {
    const parsed = parseSeasonLabel(text);
    return Number.isNaN(parsed) ? null : parsed;
  }
  const parsed = Number.parseInt(text, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return null;
  return parsed;
};

const isGoalkeeper = computed(() => {
  return String(props.position || "")
    .toLowerCase()
    .includes("goalkeeper");
});

const formatNumber = (value) => {
  return Number(value).toLocaleString("en-US");
};

const formatPercent = (value) => {
  return `${Math.round(value)}%`;
};

const createTotals = () => ({
  appearances: {
    total: 0,
    lineups: 0,
    minutes: 0,
    hasTotal: false,
    hasLineups: false,
    hasMinutes: false,
  },
  goals: {
    total: 0,
    conceded: 0,
    assists: 0,
    hasTotal: false,
    hasConceded: false,
    hasAssists: false,
  },
  shots: {
    total: 0,
    on: 0,
    totalForOn: 0,
    hasTotal: false,
    hasOn: false,
  },
  passes: {
    total: 0,
    key: 0,
    accurate: 0,
    totalForAccuracy: 0,
    hasTotal: false,
    hasKey: false,
    hasAccuracy: false,
  },
  cards: {
    yellow: 0,
    red: 0,
    hasYellow: false,
    hasRed: false,
  },
  fouls: {
    committed: 0,
    drawn: 0,
    hasCommitted: false,
    hasDrawn: false,
  },
  duels: {
    total: 0,
    won: 0,
    totalForWon: 0,
    hasTotal: false,
    hasWon: false,
  },
  tackles: {
    total: 0,
    hasTotal: false,
  },
  dribbles: {
    attempts: 0,
    success: 0,
    attemptsForSuccess: 0,
    hasAttempts: false,
    hasSuccess: false,
  },
});

const buildTotals = (statistics) => {
  const nextTotals = createTotals();
  const clubs = new Set();
  const leagues = new Set();

  statistics.forEach((stat) => {
    if (isNationalTeamStat(stat)) return;

    if (stat?.team?.name) {
      clubs.add(stat.team.name);
    }
    if (stat?.league?.name) {
      leagues.add(stat.league.name);
    }

    const appearances = toNumber(stat?.games?.appearences);
    if (appearances !== null) {
      nextTotals.appearances.total += appearances;
      nextTotals.appearances.hasTotal = true;
    }

    const lineups = toNumber(stat?.games?.lineups);
    if (lineups !== null) {
      nextTotals.appearances.lineups += lineups;
      nextTotals.appearances.hasLineups = true;
    }

    const minutes = toNumber(stat?.games?.minutes);
    if (minutes !== null) {
      nextTotals.appearances.minutes += minutes;
      nextTotals.appearances.hasMinutes = true;
    }

    const goalsTotal = toNumber(stat?.goals?.total);
    if (goalsTotal !== null) {
      nextTotals.goals.total += goalsTotal;
      nextTotals.goals.hasTotal = true;
    }

    const goalsConceded = toNumber(stat?.goals?.conceded);
    if (goalsConceded !== null) {
      nextTotals.goals.conceded += goalsConceded;
      nextTotals.goals.hasConceded = true;
    }

    const assists = toNumber(stat?.goals?.assists);
    if (assists !== null) {
      nextTotals.goals.assists += assists;
      nextTotals.goals.hasAssists = true;
    }

    const shotsTotal = toNumber(stat?.shots?.total);
    if (shotsTotal !== null) {
      nextTotals.shots.total += shotsTotal;
      nextTotals.shots.hasTotal = true;
    }

    const shotsOn = toNumber(stat?.shots?.on);
    if (shotsOn !== null) {
      nextTotals.shots.on += shotsOn;
      nextTotals.shots.hasOn = true;
    }
    if (shotsOn !== null && shotsTotal !== null) {
      nextTotals.shots.totalForOn += shotsTotal;
    }

    const passesTotal = toNumber(stat?.passes?.total);
    if (passesTotal !== null) {
      nextTotals.passes.total += passesTotal;
      nextTotals.passes.hasTotal = true;
    }

    const passesKey = toNumber(stat?.passes?.key);
    if (passesKey !== null) {
      nextTotals.passes.key += passesKey;
      nextTotals.passes.hasKey = true;
    }

    const passesAccuracy = toNumber(stat?.passes?.accuracy);
    if (passesAccuracy !== null && passesTotal !== null) {
      nextTotals.passes.accurate += (passesTotal * passesAccuracy) / 100;
      nextTotals.passes.totalForAccuracy += passesTotal;
      nextTotals.passes.hasAccuracy = true;
    }

    const cardsYellow = toNumber(stat?.cards?.yellow);
    if (cardsYellow !== null) {
      nextTotals.cards.yellow += cardsYellow;
      nextTotals.cards.hasYellow = true;
    }

    const cardsRed = toNumber(stat?.cards?.red);
    if (cardsRed !== null) {
      nextTotals.cards.red += cardsRed;
      nextTotals.cards.hasRed = true;
    }

    const foulsCommitted = toNumber(stat?.fouls?.committed);
    if (foulsCommitted !== null) {
      nextTotals.fouls.committed += foulsCommitted;
      nextTotals.fouls.hasCommitted = true;
    }

    const foulsDrawn = toNumber(stat?.fouls?.drawn);
    if (foulsDrawn !== null) {
      nextTotals.fouls.drawn += foulsDrawn;
      nextTotals.fouls.hasDrawn = true;
    }

    const duelsTotal = toNumber(stat?.duels?.total);
    if (duelsTotal !== null) {
      nextTotals.duels.total += duelsTotal;
      nextTotals.duels.hasTotal = true;
    }

    const duelsWon = toNumber(stat?.duels?.won);
    if (duelsWon !== null) {
      nextTotals.duels.won += duelsWon;
      nextTotals.duels.hasWon = true;
    }
    if (duelsWon !== null && duelsTotal !== null) {
      nextTotals.duels.totalForWon += duelsTotal;
    }

    const tacklesTotal = toNumber(stat?.tackles?.total);
    if (tacklesTotal !== null) {
      nextTotals.tackles.total += tacklesTotal;
      nextTotals.tackles.hasTotal = true;
    }

    const dribblesAttempts = toNumber(stat?.dribbles?.attempts);
    if (dribblesAttempts !== null) {
      nextTotals.dribbles.attempts += dribblesAttempts;
      nextTotals.dribbles.hasAttempts = true;
    }

    const dribblesSuccess = toNumber(stat?.dribbles?.success);
    if (dribblesSuccess !== null) {
      nextTotals.dribbles.success += dribblesSuccess;
      nextTotals.dribbles.hasSuccess = true;
    }
    if (dribblesSuccess !== null && dribblesAttempts !== null) {
      nextTotals.dribbles.attemptsForSuccess += dribblesAttempts;
    }
  });

  teamNames.value = Array.from(clubs).sort((a, b) => a.localeCompare(b));
  leagueNames.value = Array.from(leagues).sort((a, b) => a.localeCompare(b));
  return nextTotals;
};

const loadSeasonStats = async () => {
  error.value = "";
  totals.value = null;
  teamNames.value = [];
  leagueNames.value = [];

  const seasonYear = normalizeSeasonYear(props.season);
  if (!props.playerId || !seasonYear) {
    error.value = t('season-data.error-missing');
    return;
  }

  const currentRequest = (requestId += 1);
  loading.value = true;

  try {
    const statsData = await fetchPlayerSeasonStats(props.playerId, seasonYear);
    if (currentRequest !== requestId) return;

    const statistics = statsData?.response?.[0]?.statistics || [];
    if (statistics.length === 0) {
      error.value = t('season-data.error-no-stats');
      return;
    }

    totals.value = buildTotals(statistics);
  } catch (statsError) {
    if (currentRequest !== requestId) return;
    error.value = t('season-data.error-load');
  } finally {
    if (currentRequest === requestId) {
      loading.value = false;
    }
  }
};

const formatOptional = (hasValue, value) =>
  hasValue ? formatNumber(value) : "N/A";

const deriveSeasonLabel = () => {
  if (props.seasonLabel) return props.seasonLabel;
  const seasonYear = normalizeSeasonYear(props.season);
  if (!seasonYear) return t('common.unknown');
  return formatSeason(seasonYear);
};

const deriveLeagueLabel = () => {
  if (props.leagueLabel) return props.leagueLabel;
  if (leagueNames.value.length > 0) return leagueNames.value.join(", ");
  return t('common.unknown');
};

const deriveClubLabel = () => {
  if (teamNames.value.length > 0) return teamNames.value.join(", ");
  return t('common.unknown');
};

const baseRows = computed(() => {
  const rows = [];

  rows.push({
    key: "league",
    label: leagueNames.value.length > 1 ? t('season-data.stat-leagues') : t('season-data.stat-league'),
    value: deriveLeagueLabel(),
    testId: "season-meta-league",
  });

  rows.push({
    key: "season",
    label: t('season-data.stat-season'),
    value: deriveSeasonLabel(),
    testId: "season-meta-season",
  });

  rows.push({
    key: "club",
    label: teamNames.value.length > 1 ? t('season-data.stat-clubs') : t('season-data.stat-club'),
    value: deriveClubLabel(),
    testId: "season-meta-club",
  });

  if (!totals.value) return rows;

  const {
    appearances,
    goals,
    cards,
  } = totals.value;

  const hasAppearances =
    appearances.hasTotal || appearances.hasLineups || appearances.hasMinutes;
  if (hasAppearances) {
    rows.push({
      key: "games",
      label: t('season-data.stat-games'),
      value: `${formatOptional(appearances.hasTotal, appearances.total)} ${t('season-data.sub-total')} · ${formatOptional(
        appearances.hasLineups,
        appearances.lineups,
      )} ${t('season-data.sub-lineups')} · ${formatOptional(appearances.hasMinutes, appearances.minutes)} ${t('season-data.sub-min')}`,
      testId: "season-stat-games",
    });
  }

  const hasGoals = isGoalkeeper.value
    ? goals.hasConceded
    : goals.hasTotal;
  if (hasGoals) {
    rows.push({
      key: "goals",
      label: isGoalkeeper.value ? t('season-data.stat-goals-conceded') : t('season-data.stat-goals'),
      value: formatNumber(
        isGoalkeeper.value ? goals.conceded : goals.total,
      ),
      testId: "season-stat-goals",
    });
  }

  if (goals.hasAssists) {
    rows.push({
      key: "assists",
      label: t('season-data.stat-assists'),
      value: formatNumber(goals.assists),
      testId: "season-stat-assists",
    });
  }

  const hasCards = cards.hasYellow || cards.hasRed;
  if (hasCards) {
    rows.push({
      key: "cards",
      label: t('season-data.stat-cards'),
      value: `${formatOptional(cards.hasYellow, cards.yellow)} ${t('season-data.sub-yellow')} · ${formatOptional(
        cards.hasRed,
        cards.red,
      )} ${t('season-data.sub-red')}`,
      testId: "season-stat-cards",
    });
  }

  return rows;
});

const extraRows = computed(() => {
  const rows = [];

  if (!totals.value) return rows;

  const { shots, passes, fouls, duels, tackles, dribbles } = totals.value;

  const hasShots = shots.hasTotal || shots.hasOn;
  if (hasShots) {
    const onPercent =
      shots.totalForOn > 0 ? (shots.on / shots.totalForOn) * 100 : null;
    const onLabel =
      onPercent === null ? "N/A" : formatPercent(onPercent);
    rows.push({
      key: "shots",
      label: t('season-data.stat-shots'),
      value: `${formatOptional(shots.hasTotal, shots.total)} ${t('season-data.sub-total')} · ${onLabel} ${t('season-data.sub-on')}`,
      testId: "season-stat-shots",
    });
  }

  const hasPasses =
    passes.hasTotal || passes.hasKey || passes.hasAccuracy;
  if (hasPasses) {
    const accuracy =
      passes.totalForAccuracy > 0
        ? (passes.accurate / passes.totalForAccuracy) * 100
        : null;
    const accuracyLabel =
      accuracy === null ? "N/A" : formatPercent(accuracy);
    rows.push({
      key: "passes",
      label: t('season-data.stat-passes'),
      value: `${formatOptional(passes.hasTotal, passes.total)} ${t('season-data.sub-total')} · ${formatOptional(
        passes.hasKey,
        passes.key,
      )} ${t('season-data.sub-key')} · ${accuracyLabel} ${t('season-data.sub-accuracy')}`,
      testId: "season-stat-passes",
    });
  }

  const hasFouls = fouls.hasCommitted || fouls.hasDrawn;
  if (hasFouls) {
    rows.push({
      key: "fouls",
      label: t('season-data.stat-fouls'),
      value: `${formatOptional(
        fouls.hasCommitted,
        fouls.committed,
      )} ${t('season-data.sub-committed')} · ${formatOptional(fouls.hasDrawn, fouls.drawn)} ${t('season-data.sub-drawn')}`,
      testId: "season-stat-fouls",
    });
  }

  const hasDuels = duels.hasTotal || duels.hasWon;
  if (hasDuels) {
    const wonPercent =
      duels.totalForWon > 0 ? (duels.won / duels.totalForWon) * 100 : null;
    const wonLabel =
      wonPercent === null ? "N/A" : formatPercent(wonPercent);
    rows.push({
      key: "duels",
      label: t('season-data.stat-duels'),
      value: `${formatOptional(duels.hasTotal, duels.total)} ${t('season-data.sub-total')} · ${wonLabel} ${t('season-data.sub-won')}`,
      testId: "season-stat-duels",
    });
  }

  if (tackles.hasTotal) {
    rows.push({
      key: "tackles",
      label: t('season-data.stat-tackles'),
      value: formatNumber(tackles.total),
      testId: "season-stat-tackles",
    });
  }

  const hasDribbles = dribbles.hasAttempts || dribbles.hasSuccess;
  if (hasDribbles) {
    const successPercent =
      dribbles.attemptsForSuccess > 0
        ? (dribbles.success / dribbles.attemptsForSuccess) * 100
        : null;
    const successLabel =
      successPercent === null ? "N/A" : formatPercent(successPercent);
    rows.push({
      key: "dribbles",
      label: t('season-data.stat-dribbles'),
      value: `${formatOptional(dribbles.hasAttempts, dribbles.attempts)} ${t('season-data.sub-attempts')} · ${successLabel} ${t('season-data.sub-success')}`,
      testId: "season-stat-dribbles",
    });
  }

  return rows;
});

watch(
  () => [props.playerId, props.season],
  () => {
    loadSeasonStats();
    showExtra.value = false;
  },
  { immediate: true },
);
</script>
